import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCountdown } from '../hooks/useCountdown.js';

const pad = (n) => n.toString().padStart(2, '0');

// Each number slides like a ticker when its unit changes: the old digit slides
// up and out while the new one rises in from below.
const SLIDE = {
  initial: { y: '100%', opacity: 0 },
  animate: { y: '0%', opacity: 1 },
  exit: { y: '-100%', opacity: 0 },
  transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
};

// fill -> tile chrome. Every class is a literal so Tailwind's JIT generates it.
const FILLS = {
  soft: 'bg-white/80 border-slate-200 dark:bg-slate-900/70 dark:border-white/10 backdrop-blur-sm',
  tinted: 'bg-purple-50 border-purple-100 dark:bg-purple-500/10 dark:border-purple-500/20 backdrop-blur-sm',
  solid: 'bg-gradient-to-br from-primary to-secondary border-transparent shadow-md',
  glass: 'bg-slate-100/70 border-slate-200 shadow-sm dark:bg-white/10 dark:border-white/15 dark:shadow-none backdrop-blur-md',
  outline: 'bg-transparent border-primary/40 dark:border-purple-400/40',
};

// Fixed-size, overflow-clipped box so tiles don't resize as digits swap and the
// slide stays contained.
function TileNumber({ value, sm, numberClass }) {
  const inner = `absolute inset-0 flex items-center justify-center font-display font-bold tabular-nums leading-none ${
    sm ? 'text-lg' : 'text-2xl sm:text-3xl'
  } ${numberClass}`;
  return (
    <div className={`relative w-full overflow-hidden ${sm ? 'h-6' : 'h-8 sm:h-10'}`}>
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span key={value} className={inner} {...SLIDE}>
          {value}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

// The wedding countdown as tiles (Days / Hours / Mins / Secs) with a sliding
// number animation. `size` controls scale (default "md"; "sm" for the Guestbook
// lock card) and `fill` controls the tile look (default "outline"; one of
// soft / tinted / solid / glass / outline). Uses Tailwind `dark:` variants (the
// app keeps a `dark` class on <html>) so it needs no theme prop.
export function EventCountdown({ size = 'md', fill = 'outline' }) {
  const { timeLeft, hasStarted } = useCountdown();
  if (!hasStarted) return null;

  const sm = size === 'sm';
  const solid = fill === 'solid';
  const numberClass = solid
    ? 'text-white'
    : 'bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent';
  const labelClass = solid ? 'text-white/80' : 'text-slate-500 dark:text-slate-400';
  const units = [
    { v: timeLeft.days, l: 'Days' },
    { v: timeLeft.hours, l: 'Hours' },
    { v: timeLeft.minutes, l: 'Mins' },
    { v: timeLeft.seconds, l: 'Secs' },
  ];

  return (
    <div className={`flex items-stretch justify-center ${sm ? 'gap-1.5 sm:gap-2' : 'gap-2 sm:gap-3'}`}>
      {units.map((u) => (
        <div
          key={u.l}
          // Fixed, uniform widths so every tile matches and the 3-digit Days
          // value never gets clipped.
          className={`flex flex-col items-center justify-center border ${FILLS[fill] || FILLS.outline} ${
            sm ? 'rounded-xl py-1.5 w-[52px] sm:w-[58px]' : 'rounded-2xl py-2.5 w-16 sm:w-20'
          }`}
        >
          <TileNumber value={pad(u.v)} sm={sm} numberClass={numberClass} />
          <span
            className={`font-accent uppercase tracking-[0.2em] ${labelClass} ${
              sm ? 'mt-1 text-[8px]' : 'mt-1.5 text-[9px] sm:text-[10px]'
            }`}
          >
            {u.l}
          </span>
        </div>
      ))}
    </div>
  );
}
